/**
 * Pantalla de cobraments d'ADMIN (`/admin/cobraments`, S3 de `plans/stripe-payment-links.md`):
 * client de l'API, lògica pura i hooks.
 *
 * Rutes (totes només ADMIN; el backend ho exigeix):
 * - `GET payments/charges/orphans`: cobraments de Stripe que no són de cap enllaç del PMS.
 * - `GET payments/charges?from&to`: el llibre d'un rang de dies (92 com a molt).
 * - `GET payments/status` i `GET payments/events?status`: avisos de Stripe sense processar.
 * - `POST payments/events/requeue`: torna a encuar els avisos encallats (no toca diners).
 * - `POST payments/charges/:id/assign`: assignar un cobrament orfe a una reserva.
 * - `POST payments/refunds/:id/assign-line`: dir de quina habitació és una devolució.
 *
 * Res d'aquí fa devolucions ni cobra res: només llegeix i assigna. Els tipus de les respostes es
 * declaren aquí (el backend els té a `payments-admin.service.ts`); les dates arriben com a text ISO.
 */

import { addDays, differenceInCalendarDays, format, isValid, parseISO } from 'date-fns';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import type { AssignOrphanChargeResult, AssignOrphanChargeWarning, AssignRefundLineResult } from '@guilleumes/types';
import { api } from '@/services/api';
import { ORDER_STATUS_LABELS, formatDueAt } from '@/lib/payment-orders';

// ─── El que torna l'API ──────────────────────────────────────────────────────

export interface ChargeOrderView {
  id: string;
  status: string;
  kind: string;
  amountCents: number;
  groupBeds24Id: string | null;
  needsReviewAt: string | null;
  reviewReason: string | null;
}

export interface ChargeLineView {
  /** Id de la línia (habitació) de la sol·licitud. */
  id: string;
  reservationId: string;
  beds24Id: string;
  kind: string;
  amountCents: number;
  vatRateBp: number;
}

export interface ChargeRefundView {
  /** Id de la fila del llibre (el que demana `assign-line`). */
  id: string;
  providerRefundId: string;
  amountCents: number;
  status: string;
  providerCreatedAt: string;
  orderLineId: string | null;
  payoutId: string | null;
}

export interface ChargeDisputeView {
  providerDisputeId: string;
  amountCents: number;
  status: string;
  outcome: string | null;
  feeCents: number | null;
  providerCreatedAt: string;
  closedAt: string | null;
  payoutId: string | null;
}

export interface PaymentChargeView {
  id: string;
  provider: string;
  paymentIntentId: string;
  chargeId: string | null;
  status: string;
  livemode: boolean;
  amountCents: number;
  refundedCents: number;
  feeCents: number | null;
  netCents: number | null;
  currency: string;
  providerCurrency: string | null;
  providerAmountCents: number | null;
  methodType: string | null;
  failureCode: string | null;
  paidAt: string | null;
  paidDay: string | null;
  createdAt: string;
  /** `null` = cobrament orfe. */
  order: ChargeOrderView | null;
  lines: ChargeLineView[];
  linesTruncated: boolean;
  refunds: ChargeRefundView[];
  refundsTruncated: boolean;
  disputes: ChargeDisputeView[];
  disputesTruncated: boolean;
  payout: {
    id: string;
    amountCents: number;
    arrivalDate: string;
    status: string;
    automatic: boolean | null;
  } | null;
}

export interface ChargesPage {
  items: PaymentChargeView[];
  nextCursor: string | null;
  limit: number;
  /** Només a `GET payments/charges`. */
  range?: { from: string; to: string };
}

export interface UnprocessedBucket {
  total: number;
  olderThan1h: number;
  olderThan24h: number;
  oldestReceivedAt: string | null;
}

export interface PaymentsStatusView {
  environment: 'prod' | 'local';
  expectedMode: 'live' | 'test';
  gateway: {
    state: 'READY' | 'OFF';
    provider: string;
    mode: 'live' | 'test' | null;
    webhookReady: boolean;
    offReason: string | null;
    explanation: string | null;
  };
  events: {
    received: UnprocessedBucket;
    failedRecoverable: UnprocessedBucket;
    failedReview: UnprocessedBucket;
    requeueSuggested: boolean;
    reviewNeeded: boolean;
  };
}

export type PaymentEventStatus = 'RECEIVED' | 'PROCESSED' | 'IGNORED' | 'FAILED';

export interface PaymentEventView {
  id: string;
  type: string;
  status: string;
  livemode: boolean;
  providerCreatedAt: string;
  receivedAt: string;
  processedAt: string | null;
  error: string | null;
  needsReview: boolean;
  objectKind: string | null;
  objectId: string | null;
}

export interface PaymentEventsPage {
  items: PaymentEventView[];
  nextCursor: string | null;
  limit: number;
}

export interface RequeueResult {
  found: number;
  queued: number;
  nextAfterReceivedAt: string | null;
  limit: number;
  olderThanMinutes: number;
  failed: 'RECOVERABLE' | 'NONE';
}

/** El que es fa servir de `GET /reservations/by-beds24/:n` per confirmar l'assignació. */
export interface ReservationLookup {
  id: string;
  beds24_id?: string;
  guest_name?: string;
  data_entrada?: string;
  data_sortida?: string;
  nits?: number;
  estat?: string;
  preu_total?: number;
  unit?: { name?: string; displayName?: string } | null;
  room?: { name?: string } | null;
}

// ─── Claus de React Query ────────────────────────────────────────────────────

export const paymentChargesKeys = {
  all: ['payments', 'charges'] as const,
  orphans: ['payments', 'charges', 'orphans'] as const,
  list: (from: string, to: string) => ['payments', 'charges', 'list', { from, to }] as const,
  status: ['payments', 'status'] as const,
  events: (status: PaymentEventStatus) => ['payments', 'events', status] as const,
  eventsAll: ['payments', 'events'] as const,
};

// ─── Crides (fora dels hooks, per provar-les amb `api` mockejat) ─────────────

export async function fetchOrphanCharges(cursor?: string | null): Promise<ChargesPage> {
  const params = cursor ? { cursor } : {};
  return (await api.get<ChargesPage>('/payments/charges/orphans', { params })).data;
}

export async function fetchCharges(
  range: { from: string; to: string },
  cursor?: string | null,
): Promise<ChargesPage> {
  const params = { from: range.from, to: range.to, ...(cursor ? { cursor } : {}) };
  return (await api.get<ChargesPage>('/payments/charges', { params })).data;
}

export async function fetchPaymentsStatus(): Promise<PaymentsStatusView> {
  return (await api.get<PaymentsStatusView>('/payments/status')).data;
}

/** Els avisos d'un estat, del més nou al més vell (una pàgina: n'hi hauria d'haver pocs). */
export async function fetchPaymentEvents(status: PaymentEventStatus): Promise<PaymentEventsPage> {
  return (await api.get<PaymentEventsPage>('/payments/events', { params: { status, limit: 25 } }))
    .data;
}

/** Sense cos: els valors per defecte del backend (10 avisos, de més de 10 minuts, i els fallits recuperables). */
export async function requeuePaymentEvents(): Promise<RequeueResult> {
  return (await api.post<RequeueResult>('/payments/events/requeue', {})).data;
}

export async function lookupReservationByBeds24(beds24Id: string): Promise<ReservationLookup> {
  const url = `/reservations/by-beds24/${encodeURIComponent(beds24Id)}`;
  return (await api.get<ReservationLookup>(url)).data;
}

export interface AssignOrphanVariables {
  chargeId: string;
  reservationId: string;
  allowOverpayment?: boolean;
}

export async function assignOrphanCharge({
  chargeId,
  reservationId,
  allowOverpayment,
}: AssignOrphanVariables): Promise<AssignOrphanChargeResult> {
  const url = `/payments/charges/${encodeURIComponent(chargeId)}/assign`;
  const body = allowOverpayment ? { reservationId, allowOverpayment: true } : { reservationId };
  return (await api.post<AssignOrphanChargeResult>(url, body)).data;
}

export interface AssignRefundLineVariables {
  refundId: string;
  orderLineId: string | null;
}

export async function assignRefundLine({
  refundId,
  orderLineId,
}: AssignRefundLineVariables): Promise<AssignRefundLineResult> {
  const url = `/payments/refunds/${encodeURIComponent(refundId)}/assign-line`;
  return (await api.post<AssignRefundLineResult>(url, { orderLineId })).data;
}

// ─── Textos ──────────────────────────────────────────────────────────────────

const has = (object: object, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(object, key);

// L'estat d'un cobrament i el nom del mètode viuen a `payment-orders.ts` (pur): també els fa
// servir el detall de l'enllaç del popover.
export { CHARGE_STATUS_LABELS, chargeStatusLabel, methodLabel } from '@/lib/payment-orders';

export const REFUND_STATUS_LABELS: Record<string, string> = {
  SUCCEEDED: 'Feta',
  PENDING: 'En camí',
  FAILED: 'Fallida',
  CANCELED: 'Cancel·lada',
  UNKNOWN: 'Desconeguda',
};

export const refundStatusLabel = (status: string): string =>
  has(REFUND_STATUS_LABELS, status) ? REFUND_STATUS_LABELS[status] : status;

const RESERVATION_PAYMENT_LABELS: Record<string, string> = {
  PENDING: 'pendent',
  PARTIAL: 'pagada en part',
  PAID: 'pagada',
  REFUNDED: 'retornada',
};

export const reservationPaymentLabel = (status: string): string =>
  has(RESERVATION_PAYMENT_LABELS, status) ? RESERVATION_PAYMENT_LABELS[status] : status;

export const ASSIGN_WARNING_TEXTS: Record<AssignOrphanChargeWarning, string> = {
  EXCEEDS_OUTSTANDING:
    'El cobrament supera el que la reserva tenia pendent: l’enllaç nou queda «per revisar».',
  RESERVATION_CANCELLED: 'La reserva està cancel·lada: queda com a «cobrat d’una reserva cancel·lada».',
  OPEN_ORDER_REMAINS:
    'La reserva té un altre enllaç obert que segueix demanant diners: mira’l des de la reserva.',
  POLICY_UNDECIDED: 'Una part no s’ha escrit perquè depèn d’una política de devolucions no decidida.',
  RESERVATION_NOT_WRITTEN:
    'L’estat de pagament de la reserva no s’ha tocat (marcat a mà, sense preu…): revisa-la.',
};

export const assignWarningText = (warning: string): string =>
  has(ASSIGN_WARNING_TEXTS, warning)
    ? ASSIGN_WARNING_TEXTS[warning as AssignOrphanChargeWarning]
    : warning;

interface ApiErrorLike {
  message?: string;
  response?: { status?: number; data?: { code?: unknown; message?: unknown } };
}

/** El `code` del cos d'error del backend, si n'hi ha. */
export function errorCode(error: unknown): string | null {
  const code = (error as ApiErrorLike | null)?.response?.data?.code;
  return typeof code === 'string' && code ? code : null;
}

/**
 * El missatge del backend tal com ve (`message`, text o llista de textos del `ValidationPipe`).
 * Sense cos, el de la xarxa; sense res, un genèric.
 */
export function backendErrorMessage(error: unknown): string {
  const data = (error as ApiErrorLike | null)?.response?.data;
  const message = data?.message;
  if (typeof message === 'string' && message.trim()) return message.trim();
  if (Array.isArray(message)) {
    const parts = message.filter((part): part is string => typeof part === 'string' && !!part.trim());
    if (parts.length > 0) return parts.join(' · ');
  }
  const status = (error as ApiErrorLike | null)?.response?.status;
  if (status === 404) return 'No s’ha trobat.';
  const raw = (error as ApiErrorLike | null)?.message;
  return raw ? `Error: ${raw}` : 'Error inesperat';
}

export const orderStatusLabel = (status: string): string =>
  (ORDER_STATUS_LABELS as Record<string, string>)[status] ?? status;

/** La data que es llegeix: la del cobrament i, si no en té (fallit o pendent), la de quan es va desar. */
export const chargeDateText = (charge: PaymentChargeView): string =>
  formatDueAt(charge.paidAt ?? charge.createdAt);

export const NOT_ASSIGNABLE_TEXT = 'Només es pot assignar un cobrament confirmat.';

// ─── Lògica pura ─────────────────────────────────────────────────────────────

/** Només es pot assignar un cobrament confirmat (el backend ho exigeix). */
export const canAssignCharge = (charge: PaymentChargeView): boolean => charge.status === 'SUCCEEDED';

/** El número de reserva de Beds24 que s'escriu al camp: 8 xifres (espais fora). */
export function normalizeBeds24Input(text: string | null | undefined): string | null {
  const clean = (text ?? '').replace(/\s+/g, '');
  return /^\d{8}$/.test(clean) ? clean : null;
}

/** Els dies que es poden demanar al llibre d'un cop (el sostre del backend). */
export const MAX_RANGE_DAYS = 92;
export const DEFAULT_RANGE_DAYS = 31;

/** Per defecte, els 31 dies que acaben avui (hora del navegador, que és la de l'hotel). */
export function defaultChargesRange(now: Date = new Date()): { from: string; to: string } {
  return {
    from: format(addDays(now, -(DEFAULT_RANGE_DAYS - 1)), 'yyyy-MM-dd'),
    to: format(now, 'yyyy-MM-dd'),
  };
}

/** Per què no es pot demanar aquest rang, o `null` si es pot (les mateixes regles que el backend). */
export function rangeProblem(from: string, to: string): string | null {
  const a = parseISO(from);
  const b = parseISO(to);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !isValid(a)) return 'Falta el primer dia.';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(to) || !isValid(b)) return 'Falta l’últim dia.';
  const days = differenceInCalendarDays(b, a) + 1;
  if (days < 1) return 'L’últim dia és abans del primer.';
  if (days > MAX_RANGE_DAYS) {
    return `El rang és de ${days} dies i el màxim és de ${MAX_RANGE_DAYS} (un trimestre).`;
  }
  return null;
}

/** El cobrament és d'un enllaç «per revisar». */
export const isChargeForReview = (charge: PaymentChargeView): boolean =>
  charge.order?.needsReviewAt != null;

export type ChargesFilter = 'all' | 'review' | 'orphans';

export function filterCharges(
  charges: readonly PaymentChargeView[],
  filter: ChargesFilter,
): PaymentChargeView[] {
  if (filter === 'review') return charges.filter(isChargeForReview);
  if (filter === 'orphans') return charges.filter((charge) => charge.order === null);
  return [...charges];
}

export interface UnassignedRefund {
  charge: PaymentChargeView;
  refund: ChargeRefundView;
}

/**
 * L'enllaç del cobrament és de més d'una reserva (un grup). Amb les línies retallades no se sap:
 * es compta com a grup.
 */
export const isMultiReservationCharge = (charge: PaymentChargeView): boolean =>
  charge.linesTruncated || new Set(charge.lines.map((line) => line.reservationId)).size > 1;

/**
 * Les devolucions sense habitació d'un cobrament amb enllaç de més d'una reserva. Les d'un enllaç
 * d'una sola reserva no: no hi ha dubte de quina és. Les d'un orfe tampoc: primer cal assignar el
 * cobrament (el backend respon `REFUND_CHARGE_ORPHAN`). Les fallides o cancel·lades no resten res
 * i tampoc s'ensenyen.
 */
export function unassignedRefunds(charges: readonly PaymentChargeView[]): UnassignedRefund[] {
  const out: UnassignedRefund[] = [];
  for (const charge of charges) {
    if (!charge.order || charge.lines.length === 0 || !isMultiReservationCharge(charge)) continue;
    for (const refund of charge.refunds) {
      if (refund.orderLineId !== null) continue;
      if (refund.status === 'FAILED' || refund.status === 'CANCELED') continue;
      out.push({ charge, refund });
    }
  }
  return out;
}

/** Una opció de «De quina habitació és?»: una per reserva. */
export interface RefundLineOption {
  /** La línia que s'envia a `assign-line` (la d'estada, si n'hi ha). */
  lineId: string;
  reservationId: string;
  beds24Id: string;
  /** El que es va demanar d'aquella reserva: estada + part de més. */
  amountCents: number;
}

/** Tipus de línia que, dins d'una reserva, són una sola bossa (com al llibre del backend). */
const RESERVATION_MONEY_KINDS: readonly string[] = ['STAY', 'EXTRA'];

/**
 * Les opcions per dir de quina habitació és una devolució: UNA per reserva, que cobreix l'estada i
 * la part de més del pendent (`EXTRA`; 14-OCT-2026). El backend reparteix una devolució assignada a
 * la línia d'estada entre totes dues. Una línia d'un altre tipus (taxa turística) va sola.
 */
export function refundLineOptions(lines: readonly ChargeLineView[]): RefundLineOption[] {
  const options: RefundLineOption[] = [];
  const byReservation = new Map<string, RefundLineOption>();
  for (const line of lines) {
    const bag = RESERVATION_MONEY_KINDS.includes(line.kind);
    const existing = bag ? byReservation.get(line.reservationId) : undefined;
    if (existing) {
      existing.amountCents += line.amountCents;
      if (line.kind === 'STAY') existing.lineId = line.id;
      continue;
    }
    const option = {
      lineId: line.id,
      reservationId: line.reservationId,
      beds24Id: line.beds24Id,
      amountCents: line.amountCents,
    };
    options.push(option);
    if (bag) byReservation.set(line.reservationId, option);
  }
  return options;
}

export interface StuckEventsSummary {
  /** Desats i encara per processar. */
  received: number;
  /** D'aquests, fa més d'una hora. */
  receivedOld: number;
  /** Fallits que reintentar pot arreglar. */
  failedRecoverable: number;
  /** Fallits que ha de mirar una persona (no es reencuen). */
  failedReview: number;
  /** Hi ha alguna cosa que el botó «Torna a encuar» pot moure. */
  canRequeue: boolean;
  /** No hi ha res encallat ni per mirar. */
  allClear: boolean;
}

export function stuckEventsSummary(status: PaymentsStatusView | null | undefined): StuckEventsSummary {
  const events = status?.events;
  const received = events?.received.total ?? 0;
  const failedRecoverable = events?.failedRecoverable.total ?? 0;
  const failedReview = events?.failedReview.total ?? 0;
  return {
    received,
    receivedOld: events?.received.olderThan1h ?? 0,
    failedRecoverable,
    failedReview,
    canRequeue: received + failedRecoverable > 0,
    allClear: received + failedRecoverable + failedReview === 0,
  };
}

export function requeueResultText(result: RequeueResult): string {
  if (result.found === 0) {
    return `No n’hi ha cap de més de ${result.olderThanMinutes} minuts per tornar a encuar.`;
  }
  const more = result.nextAfterReceivedAt ? ' En queden més: torna-ho a prémer.' : '';
  return `S’han tornat a encuar ${result.queued} de ${result.found} avisos.${more}`;
}

/** El que es diu de la reserva trobada abans de confirmar (sense telèfon ni document). */
export function reservationLookupText(reservation: ReservationLookup): string {
  const parts: string[] = [];
  const room = reservation.unit?.displayName || reservation.unit?.name || reservation.room?.name;
  if (room) parts.push(room);
  const day = (iso: string | undefined) => {
    if (!iso) return null;
    const date = new Date(iso);
    return isValid(date) ? format(date, 'dd/MM/yyyy') : null;
  };
  const from = day(reservation.data_entrada);
  const to = day(reservation.data_sortida);
  if (from && to) parts.push(`${from} → ${to}`);
  if (reservation.estat) parts.push(reservation.estat);
  return parts.join(' · ');
}

// ─── Després d'escriure ──────────────────────────────────────────────────────

/** Assignar canvia el llibre, l'enllaç i l'estat de pagament de la reserva. */
export function invalidateAfterAssign(queryClient: Pick<QueryClient, 'invalidateQueries'>): void {
  void queryClient.invalidateQueries({ queryKey: paymentChargesKeys.all });
  void queryClient.invalidateQueries({ queryKey: ['payment-orders'] });
  void queryClient.invalidateQueries({ queryKey: ['calendar-bookings'], exact: false });
  void queryClient.invalidateQueries({ queryKey: ['reservation-full'], exact: false });
  void queryClient.invalidateQueries({ queryKey: ['reservations'], exact: false });
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

export function useOrphanCharges() {
  return useInfiniteQuery({
    queryKey: paymentChargesKeys.orphans,
    queryFn: ({ pageParam }) => fetchOrphanCharges(pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last: ChargesPage) => last.nextCursor,
    staleTime: 30_000,
  });
}

export function useCharges(range: { from: string; to: string }, enabled = true) {
  return useInfiniteQuery({
    queryKey: paymentChargesKeys.list(range.from, range.to),
    queryFn: ({ pageParam }) => fetchCharges(range, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last: ChargesPage) => last.nextCursor,
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

export function usePaymentsStatus() {
  return useQuery({
    queryKey: paymentChargesKeys.status,
    queryFn: fetchPaymentsStatus,
    staleTime: 30_000,
  });
}

export function usePaymentEvents(status: PaymentEventStatus, enabled = true) {
  return useQuery({
    queryKey: paymentChargesKeys.events(status),
    queryFn: () => fetchPaymentEvents(status),
    enabled,
    staleTime: 30_000,
  });
}

export function useRequeuePaymentEvents() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: requeuePaymentEvents,
    onSuccess: (result: RequeueResult) => {
      toast.success(requeueResultText(result));
      void queryClient.invalidateQueries({ queryKey: paymentChargesKeys.status });
      void queryClient.invalidateQueries({ queryKey: paymentChargesKeys.eventsAll });
    },
    onError: (error: unknown) => {
      toast.error(backendErrorMessage(error));
    },
  });
}

/** Busca la reserva pel número de Beds24. És una lectura, però surt d'un botó: va com a mutació. */
export function useReservationLookup() {
  return useMutation({ mutationFn: lookupReservationByBeds24 });
}

/** Assignar un orfe. L'error no fa toast: el diàleg l'ensenya tal com ve (i ofereix seguir si cal). */
export function useAssignOrphanCharge() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: assignOrphanCharge,
    onSuccess: (result: AssignOrphanChargeResult) => {
      invalidateAfterAssign(queryClient);
      toast.success(
        result.alreadyAssigned
          ? 'Ja estava assignat a aquesta reserva: no s’ha creat res.'
          : `Cobrament assignat. La reserva queda ${reservationPaymentLabel(result.reservationPaymentStatus)}.`,
      );
      for (const warning of result.warnings) toast.warning(assignWarningText(warning));
    },
  });
}

export function useAssignRefundLine() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: assignRefundLine,
    onSuccess: (result: AssignRefundLineResult) => {
      invalidateAfterAssign(queryClient);
      toast.success(
        result.orderLineId === null
          ? 'La devolució ja no és de cap habitació.'
          : 'Devolució assignada a l’habitació.',
      );
    },
  });
}
